"use strict";
exports.__esModule = true;
//#endregion
/**
 * Should be used with {{Use}}
 */
var Trait = /** @class */ (function () {
    function Trait() {
    }
    return Trait;
}());
exports.Trait = Trait;
/**
 * Produce a token depending of the property/method selected.
 * e.g.
 * ```ts
 *  class Trait {
 *      public static STATIC_METHOD; // Will produce this token: `Trait::STATIC_METHOD`
 *      public instanceProp; // Will produce this token: `Trait.instanceProp`
 *  }
 * ```
 *
 * @param trait The trait in which the member name will be selected
 * @param name  The member name itself. Intellisense will provide you a correct list to avoid mistakes
 * @param staticMember If the member to select is static.
 */
function traitSelector(trait, name, staticMember) {
    var traitName = trait.name;
    if (staticMember) {
        if (name in trait)
            return traitName + "::" + name;
        throw new Error("Error on TraitSelector. Static member \"" + name + "\" was not found in trait \"" + traitName + "\", or is not instanciated, or is not public.");
    }
    if (name in trait.prototype)
        return traitName + "." + name;
    throw new Error("Error on TraitSelector. Member \"" + name + "\" was not found in trait \"" + traitName + "\" or is not public.");
}
exports.traitSelector = traitSelector;
var SCOPES = ['public', 'protected', 'private'];
var NAME_REGEX = /^([a-zA-Z_]\w*)*$/i;
function isScope(scope) {
    return SCOPES.includes(scope);
}
/**
 * Validate and parse the TraitConfig.
 *
 * @internal
 */
function traitConfigParser(config, traits, target) {
    var ret = [];
    function selectorParser(selector) {
        var isStatic = selector.includes('::');
        var parts = selector.split(isStatic ? '::' : '.');
        if (parts.length !== 2) {
            throw new Error("The selector \"" + selector + "\" is malformed and should be either \"<class>.<propOrMethod>\" or \"<class>::<staticPropOrMethod>\".");
        }
        var klass = parts[0], name = parts[1];
        var t = traits.filter(function (trait) { return trait.name === klass; })[0];
        if (isStatic && !t[name]) {
            throw new Error("In selector \"" + selector + "\", the static property or method \"" + name + "\" was not found on the trait \"" + klass + "\".");
        }
        else if (!isStatic && !t.prototype[name]) {
            throw new Error("In selector \"" + selector + "\", the property or method \"" + name + "\" was not found on the trait \"" + klass + "\" prototype.");
        }
        return [klass, name, isStatic];
    }
    function changeAsParser(changeAs) {
        var parts = changeAs.split(' ');
        var scope = null;
        var part = parts[0];
        var name = parts[1];
        if (parts.length === 1) {
            // if only one part is found, can be a scope or a name
            if (isScope(part)) {
                scope = part;
                name = null;
            }
            else {
                name = part;
            }
        }
        else if (parts.length === 2) {
            // if two parts are found, will be `<scope> <name>`
            if (!isScope(part)) {
                throw new Error("In the \"as\"-part \"" + changeAs + "\", the scope have to be one of these: " + SCOPES.join(', '));
            }
            scope = part;
        }
        else {
            throw new Error("The \"as\"-part \"" + changeAs + "\" is not valid and should be like \"<scope> <name>\", or \"<scope>\", or \"<name>\" instead. (e.g. \"public myProp\")");
        }
        if (name !== null && !NAME_REGEX.test(name)) {
            throw new Error("In the \"as\"-part \"" + changeAs + "\", the name \"" + name + "\" is not a valid property not method name. It should respect the following regex: \"" + NAME_REGEX + "\"");
        }
        return [scope, name];
    }
    // handle 'InsteadOf'
    var insteadOf = config.insteadOf || {};
    for (var _i = 0, _a = Object.entries(insteadOf); _i < _a.length; _i++) {
        var _b = _a[_i], selector = _b[0], ioTraits = _b[1];
        var _c = selectorParser(selector), klassFrom = _c[0], name_1 = _c[1], isStatic = _c[2];
        ret.push({
            isStatic: isStatic,
            klassFrom: klassFrom,
            name: name_1,
            klassTo: ioTraits.map(function (t) { return t.name; }),
            rule: 'insteadof'
        });
    }
    // handle 'As'
    var as = config.as || {};
    var _loop_1 = function (selector, changeAs) {
        var _a = selectorParser(selector), klass = _a[0], oldName = _a[1], isStatic = _a[2];
        var _b = changeAsParser(changeAs), scope = _b[0], newName = _b[1];
        if (newName === null)
            return "continue"; // WIP Handle scope
        var traitForName = traits.filter(function (t) { return t.name === klass; })[0];
        if (isStatic) {
            if (typeof traitForName[newName] !== 'undefined') {
                throw new Error("Collision on \"as\" trait rule. \"" + klass + "." + newName + "\" (static) already exists and can't be overlap.");
            }
            else if (typeof target[newName] !== 'undefined') {
                throw new Error("Collision on \"as\" trait rule. \"" + target.name + "." + newName + "\" (static) already exists and can't be overlap.");
            }
        }
        else {
            if (typeof traitForName.prototype[newName] !== 'undefined') {
                throw new Error("Collision on \"as\" trait rule. \"" + klass + "." + newName + "\" already exists and can't be overlap.");
            }
            else if (typeof target.prototype[newName] !== 'undefined') {
                throw new Error("Collision on \"as\" trait rule. \"" + target.name + "." + newName + "\" already exists and can't be overlap.");
            }
        }
        ret.push({
            isStatic: isStatic,
            klass: klass,
            newName: newName,
            oldName: oldName,
            scope: scope,
            rule: 'as'
        });
    };
    for (var _d = 0, _e = Object.entries(as); _d < _e.length; _d++) {
        var _f = _e[_d], selector = _f[0], changeAs = _f[1];
        _loop_1(selector, changeAs);
    }
    return ret;
}
//#endregion
/**
 * Implements the "use" keyword from PHP with a `Trait`.
 *
 * @param traits The trait implementation to use or an array of trais followed by a trait config at the last element.
 * @returns The class "traited"
 *
 * @annotation
 */
function Use(traits) {
    return function (clazz) {
        if (!Array.isArray(traits)) {
            var defaultConf = {};
            traits = [traits, defaultConf];
        }
        var config = traitConfigParser(traits.pop(), traits, clazz);
        var doNotUse = {}; // className [ [prop, isStatic] ]
        var aliases = {}; // className [ [prop, alias, isStatic] ]
        for (var _i = 0, config_1 = config; _i < config_1.length; _i++) {
            var rule = config_1[_i];
            if (rule.rule === 'insteadof') {
                for (var _a = 0, _b = rule.klassTo; _a < _b.length; _a++) {
                    var to = _b[_a];
                    if (!doNotUse[to]) {
                        doNotUse[to] = [];
                    }
                    doNotUse[to].push([rule.name, rule.isStatic]);
                }
            }
            else {
                // TODO: handle scope
                if (!aliases[rule.klass]) {
                    aliases[rule.klass] = [];
                }
                aliases[rule.klass].push([rule.oldName, rule.newName, rule.isStatic]);
            }
        }
        for (var _c = 0, _d = traits; _c < _d.length; _c++) {
            var trait = _d[_c];
            var filters = [copyProperties.DEFAULT_FILTER];
            var filtersStatic = [copyProperties.DEFAULT_FILTER];
            var dnu = doNotUse[trait.name] || [];
            for (var _e = 0, dnu_1 = dnu; _e < dnu_1.length; _e++) {
                var _f = dnu_1[_e], name_2 = _f[0], isStatic = _f[1];
                (isStatic ? filtersStatic : filters).push(new RegExp(name_2));
            }
            var traitClone = {};
            var traitProtoClone = {};
            copyProperties(traitClone, trait);
            copyProperties(traitProtoClone, trait.prototype);
            var al = aliases[trait.name] || [];
            for (var _g = 0, al_1 = al; _g < al_1.length; _g++) {
                var _h = al_1[_g], member = _h[0], alias = _h[1], isStatic = _h[2];
                if (isStatic) {
                    traitClone[alias] = traitClone[member];
                    delete traitClone[member];
                }
                else {
                    traitProtoClone[alias] = traitProtoClone[member];
                    delete traitProtoClone[member];
                }
            }
            copyProperties(clazz, al.length ? traitClone : trait, filtersStatic);
            copyProperties(clazz.prototype, al.length ? traitProtoClone : trait.prototype, filters);
        }
        return clazz;
    };
}
exports.Use = Use;
/**
 * Copy param from a `source` into the `target`.
 *
 * Used to properly mix class with prototypes and statics members.
 *
 * @param filters An array of regexp to omit some members to be copied. (/(prototype|name|constructor)/ by default).
 */
function copyProperties(target, source, filters) {
    if (filters === void 0) { filters = [copyProperties.DEFAULT_FILTER]; }
    var ownPropertyNames = Object.getOwnPropertyNames(source);
    ownPropertyNames
        .filter(function (key) { return filters.every(function (f) { return !f.test(key); }) && typeof target[key] === 'undefined'; })
        .forEach(function (key) {
        var desc = Object.getOwnPropertyDescriptor(source, key);
        try {
            Object.defineProperty(target, key, desc);
        }
        catch (_a) {
            /* catch for IE11 - no op */
        }
    });
}
exports.copyProperties = copyProperties;
copyProperties.DEFAULT_FILTER = /(prototype|name|constructor)/;
